import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { translate, TranslocoModule } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LetDirective } from '@ngrx/component';
import { cloneDeep } from 'lodash';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { MessagesModule } from 'primeng/messages';
import { PasswordModule } from 'primeng/password';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { filter, map, startWith, tap } from 'rxjs/operators';

import { UserData } from '@pwp-common';

import { FormFieldHelpComponent } from '../../../../components/form/form-field-help/form-field-help.component';
import { AuthService } from '../../../../services/user/auth/auth.service';
import { UserDataService } from '../../../../services/user/user-data/user-data.service';
import { FirebaseAuthAction } from '../../enums/firebase-auth-action';
import { AuthenticationService } from '../../services/authentication/authentication.service';

@UntilDestroy()
@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    ButtonModule,
    CheckboxModule,
    CommonModule,
    FormFieldHelpComponent,
    FormsModule,
    InputTextModule,
    LetDirective,
    MessagesModule,
    PasswordModule,
    ReactiveFormsModule,
    RouterModule,
    TranslocoModule,
  ],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MessageService],
})
export class LoginComponent implements OnInit {
  private readonly authenticationService = inject(AuthenticationService);

  private readonly authService = inject(AuthService);

  private readonly fb = inject(NonNullableFormBuilder);

  private readonly messageService = inject(MessageService);

  private readonly userDataService = inject(UserDataService);

  private readonly angularFireAuth = inject(AngularFireAuth);

  private readonly activatedRoute = inject(ActivatedRoute);

  private readonly router = inject(Router);

  // @Todo: handle this by implementing a custom Transloco missing key handler.
  // Need to discuss strategy between returning empty or passing a parameter.
  private readonly knownSignInErrorCodes = new Set([
    'auth/invalid-login-credentials',
    'auth/network-request-failed',
    'auth/too-many-requests',
    'auth/user-disabled',
  ]);

  public readonly form = this.fb.group({
    email: this.fb.control('', { validators: [Validators.required, Validators.email] }),
    password: this.fb.control('', { validators: [Validators.required] }),
    acceptTermsAndPrivacyPolicy: this.fb.control(false, { validators: [Validators.requiredTrue] }),
  });

  public readonly loading$ = new BehaviorSubject(false);

  public readonly showPassword$ = new BehaviorSubject(false);

  public readonly isVerifyEmailMode$ = new BehaviorSubject(false);

  public ngOnInit(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        startWith(null),
        map(() => this.activatedRoute.snapshot.data?.mode),
        tap((mode) => {
          const isVerifyMode = mode === FirebaseAuthAction.verifyAndChangeEmail;
          this.isVerifyEmailMode$.next(isVerifyMode);
          if (isVerifyMode) {
            this.handleVerifyAndChangeEmail().catch((error) => {
              console.error('Email verification failed:', error);
            });
          }
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  public toggleShowPassword(): void {
    this.showPassword$.next(!this.showPassword$.value);
  }

  private async syncAuthEmailAndDbEmail(data: UserData): Promise<void> {
    const userData = cloneDeep(data);
    const authUser = await this.angularFireAuth.currentUser;

    if (userData.getNotificationEmail() === authUser?.email) {
      console.log('syncAuthEmailAndDbEmail: Emails are in sync');
      return;
    }

    userData.setNotificationEmail(authUser?.email ?? '');
    await firstValueFrom(this.userDataService.upload({ obj: userData }));
  }

  private async handleVerifyAndChangeEmail(): Promise<void> {
    const oobCode = this.activatedRoute.snapshot.queryParams?.oobCode;
    if (!oobCode) {
      this.messageService.clear();
      this.messageService.add({
        severity: 'error',
        detail: translate('login.verify_email_failed'),
      });
      return;
    }

    try {
      await this.angularFireAuth.applyActionCode(oobCode);

      const currentUser = await this.angularFireAuth.currentUser;
      if (currentUser) {
        const userData = await firstValueFrom(this.userDataService.getUserData());
        await this.syncAuthEmailAndDbEmail(userData);
      }

      this.messageService.clear();
      this.messageService.add({
        severity: 'success',
        detail: translate('login.email_updated'),
      });
    } catch (error) {
      console.error('Email verification failed:', error);
      this.messageService.clear();
      this.messageService.add({
        severity: 'error',
        detail: translate('login.verify_email_failed'),
      });
    }
  }

  public async login(): Promise<void> {
    this.loading$.next(true);
    const { email, password } = this.form.value;

    try {
      await this.authenticationService.signIn(email, password);
      const userData = await firstValueFrom(this.userDataService.getUserData());
      await this.syncAuthEmailAndDbEmail(userData);
      this.authService.showSetStatusDialog$.next(true);
    } catch (e) {
      const errorCode = this.knownSignInErrorCodes.has(e.code) ? e.code : 'unknown';
      console.error('LoginComponent.login: Sign-in failed', { errorCode: e.code, email, error: e });

      this.messageService.clear();
      this.messageService.add({
        severity: 'error',
        detail: translate(`login.signInError.${errorCode}`),
      });
    } finally {
      this.loading$.next(false);
    }
  }
}
